import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import { useToastManager } from "@porsche-design-system/components-react"
import type {
	ActionItem,
	ChangePerformanceKitView,
	ChangeWheelsView,
	DetailsEquipmentStageView,
	DetailsEquipmentView,
	EquipmentOptionView,
	ModelInformationView,
} from "@slfinrtl/ui-bff-routes"
import {
	getDetailsEquipmentStageRoute,
	getEquipmentListRoute,
	getPerformanceKitRoute,
	getWheelsRoute,
} from "@slfinrtl/ui-bff-routes"
import { useCallback, useEffect, useState } from "react"
import { defineMessages, useIntl } from "react-intl"
import { useBff } from "src/hooks"
import { useVehicleDetailsContext } from "src/screens/vehicleDetails/context/VehicleDetailsContext"
import type { EditEquipmentView } from "../../specialEquipmentEditContainer/hooks/useSpecialEquipmentEdit/useSpecialEquipmentEdit"

const messages = defineMessages({
	failedToLoadEditData: {
		defaultMessage: "Unable to load edit mode. Please try again later.",
		id: "vehicleDetails.overview.stage.failedToLoadEditData",
	},
})

interface UseSpecialEquipmentStageReturnType {
	editAction: ActionItem | undefined
	editEquipmentView: EditEquipmentView | undefined
	handleClick: () => Promise<void>
	isLoading: boolean
	leaveEditStage: (viewUpdate: DetailsEquipmentView | undefined) => void
	view: DetailsEquipmentView | undefined | null
}

export const useSpecialEquipmentStage = (): UseSpecialEquipmentStageReturnType => {
	const { formatMessage } = useIntl()
	const { addMessage } = useToastManager()

	const [isLoadingEditView, setIsLoadingEditView] = useState<boolean>(false)
	const [stageView, setStageView] = useState<DetailsEquipmentStageView | null>()
	const [editEquipmentView, setEditEquipmentView] = useState<{
		equipmentList: EquipmentOptionView[]
		performanceKit: ChangePerformanceKitView
		wheels: ChangeWheelsView
	}>()

	const [modelInformationView, setModelInformationView] = useState<ModelInformationView | null>()

	const { inventoryId, setEditStage, subscribe } = useVehicleDetailsContext()
	const { availableLocales } = useDealerContext()

	useEffect(() => subscribe("modelInformationChanged", setModelInformationView), [subscribe])

	const fetchEquipment = useBff(
		getDetailsEquipmentStageRoute,
		{
			body: undefined,
			pathParams: {},
			queryParams: {
				"inventory-id": inventoryId,
			},
		},
		"inline",
	)

	useEffect(() => {
		fetchEquipment()
			.then(setStageView)
			.catch(() => setStageView(null))
	}, [fetchEquipment])

	const leaveEditStage = useCallback(
		(viewUpdate: DetailsEquipmentView | undefined) => {
			if (viewUpdate) {
				setStageView((prev) => ({
					...prev,
					view: viewUpdate,
				}))
			}
			setEditStage(undefined)
		},
		[setStageView, setEditStage],
	)

	const getWheels = useBff(
		getWheelsRoute,
		{
			body: undefined,
			pathParams: {},
			queryParams: {
				"inventory-id": inventoryId,
				modelYear: modelInformationView?.modelYear?.toString(),
				orderTypeCode: modelInformationView?.orderTypeCode,
			},
		},
		"inline",
	)

	const getPerformanceKit = useBff(
		getPerformanceKitRoute,
		{
			body: undefined,
			pathParams: {},
			queryParams: {
				"inventory-id": inventoryId,
			},
		},
		"inline",
	)

	const getEquipmentList = useBff(
		getEquipmentListRoute,
		{
			body: undefined,
			pathParams: {},
			queryParams: {
				"inventory-id": inventoryId,
				"market-languages": availableLocales,
			},
		},
		"inline",
	)

	const handleEditClick = useCallback(() => {
		setIsLoadingEditView(true)
		return Promise.all([getWheels(), getPerformanceKit(), getEquipmentList()])
			.then(([wheels, performanceKit, equipmentList]) => {
				setEditEquipmentView({ equipmentList, performanceKit, wheels })
				setEditStage("equipment")
			})
			.catch(() =>
				addMessage({
					state: "info",
					text: formatMessage(messages.failedToLoadEditData),
				}),
			)
			.finally(() => setIsLoadingEditView(false))
	}, [addMessage, formatMessage, getEquipmentList, getWheels, getPerformanceKit, setEditStage, setIsLoadingEditView])

	return {
		editAction: stageView?.editAction,
		editEquipmentView,
		handleClick: handleEditClick,
		isLoading: isLoadingEditView,
		leaveEditStage,
		view: stageView === null ? null : stageView?.view,
	}
}
